// src/components/ProjectCard.jsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProjectCard({ p, onOpen }) {
  const [showMedia, setShowMedia] = useState(false);
  const hasMedia = p.media && p.media.url;

  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => hasMedia && setShowMedia(true)}
      onMouseLeave={() => setShowMedia(false)}
      className="group relative w-full h-full min-h-[320px] text-left border-l-4 border-slate-900 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 transition-all duration-300
                 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 overflow-hidden"
    >
      {/* Media Preview Overlay - Shows on hover */}
      <AnimatePresence>
        {hasMedia && showMedia && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-10 bg-slate-900 dark:bg-slate-950 flex items-center justify-center p-4"
          >
            {p.media.type === 'video' ? (
              <video
                src={p.media.url}
                autoPlay
                loop
                muted
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <motion.img
                src={p.media.url}
                alt={p.title}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
                className="max-w-full max-h-full object-contain"
              />
            )}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="absolute bottom-4 right-4 px-3 py-1.5 bg-yellow-400 text-slate-900 text-xs font-mono font-bold border-2 border-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
            >
              PREVIEW
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Content */}
      <div className="flex h-full flex-col relative z-0">
        {/* Title & Featured - Fixed height */}
        <div className="mb-2 min-h-[52px]">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 leading-tight line-clamp-2 mb-1 flex-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {p.title}
            </h3>
            {hasMedia && (
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="shrink-0 relative"
              >
                <svg
                  className="w-5 h-5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white dark:border-slate-800 animate-pulse"></span>
              </motion.div>
            )}
          </div>
          {p.featured && (
            <motion.span
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              whileHover={{ scale: 1.05 }}
              className="inline-block text-[11px] font-mono font-black px-2 py-1 bg-yellow-400 text-slate-900 uppercase tracking-wider border-2 border-slate-900 dark:border-slate-600 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-shadow"
            >
              ★ FEATURED
            </motion.span>
          )}
        </div>

        {/* Date - Fixed height */}
        <div className="min-h-[20px] mb-2">
          {p.completion_date && (
            <div className="text-xs font-mono text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {new Date(p.completion_date).toLocaleDateString('en', { month: 'short', year: 'numeric' })}
            </div>
          )}
        </div>

        {/* Category Tags - Fixed height */}
        <div className="min-h-[24px] mb-2">
          {p.tags && p.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {p.tags.slice(0, 3).map((tag, idx) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 uppercase leading-none hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors cursor-default"
                >
                  {tag}
                </motion.span>
              ))}
            </div>
          )}
        </div>

        {/* Description - Fixed height with line clamp */}
        <div className="mb-3 flex-1">
          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-4 leading-relaxed group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">
            {p.description}
          </p>
        </div>

        {/* Impact Metrics Preview */}
        {p.impact && (
          <div className="mb-3 flex gap-1 flex-wrap">
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="text-[9px] font-bold px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 uppercase leading-none cursor-default"
            >
              ✓ Impact Metrics
            </motion.span>
          </div>
        )}

        {/* Tech stack - Fixed at bottom */}
        <div className="mt-auto pt-2 border-t border-slate-200 group-hover:border-blue-200 transition-colors">
          <div className="text-[11px] font-mono text-slate-500 line-clamp-2 mb-2 group-hover:text-slate-600 transition-colors">
            {(p.technologies || []).slice(0, 5).join(' · ')}
            {(p.technologies || []).length > 5 && ` +${(p.technologies || []).length - 5}`}
          </div>

          {/* Hover indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            className="text-xs font-mono text-blue-600 transition-opacity"
          >
            {hasMedia ? 'Hover for preview • Click for details →' : 'Click for details →'}
          </motion.div>
        </div>
      </div>
    </button>
  );
}
